import { authorization } from "module/auth";

const ANON_ID_STORAGE_KEY = "liveblocks-anon-id";

export const getOrCreateAnonymousId = (): string => {
  try {
    const existing = window.localStorage.getItem(ANON_ID_STORAGE_KEY);
    if (existing) return existing;
    const generated =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const id = `anonymous-${generated}`;
    window.localStorage.setItem(ANON_ID_STORAGE_KEY, id);
    return id;
  } catch {
    return `anonymous-${Math.random().toString(36).slice(2)}`;
  }
};

export type LiveblocksUserPayload = {
  id?: string;
  name?: string;
  avatar?: string;
};

export const buildLiveblocksUserPayload = (user: any): LiveblocksUserPayload => {
  if (!user) {
    return { id: getOrCreateAnonymousId(), name: "Guest" };
  }
  // Never fall back to `clientName` — that's the company name, not the user's
  // name, and it gets broadcast as presence.info.name to every other client
  // in the room. Prefer real first+last, then email, then nothing (resolved
  // later via the /platform/users/resolve endpoint).
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || undefined;
  return {
    id: user.id ? `user-${user.id}` : getOrCreateAnonymousId(),
    name,
    avatar: user.profilePicture || undefined,
  };
};

/**
 * Authoritative thread deletion that bypasses Liveblocks' "all comments must
 * be yours" rule. The `oppizi-api` endpoint validates authorship server-side
 * with its admin secret, so the original author can delete a thread even if
 * other users replied. Use this instead of `useDeleteThread()` for that case.
 */
export const deleteThreadAuthoritatively = async ({
  roomId,
  threadId,
}: {
  roomId: string;
  threadId: string;
}): Promise<void> => {
  const authUrl = process.env.REACT_APP_LIVEBLOCKS_AUTH_URL;
  if (!authUrl) throw new Error("REACT_APP_LIVEBLOCKS_AUTH_URL is not configured");
  // The auth URL ends in `/auth`; swap the suffix for our delete endpoint.
  const deleteUrl = authUrl.replace(/\/auth(?:\/?$)/, "/delete-thread");

  const apiKey = authorization.getAPIKey?.();
  const response = await fetch(deleteUrl, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
    },
    body: JSON.stringify({ roomId, threadId }),
  });

  if (!response.ok) {
    const errBody = await response.text().catch(() => "");
    throw new Error(`Delete thread failed (${response.status}): ${errBody}`);
  }
};

export const buildLiveblocksAuthEndpoint =
  (url: string | undefined, userPayload?: LiveblocksUserPayload) => async (room?: string) => {
    if (!url) {
      throw new Error("REACT_APP_LIVEBLOCKS_AUTH_URL is not configured");
    }

    const apiKey = authorization.getAPIKey?.();
    const resolvedUser = userPayload || buildLiveblocksUserPayload(authorization.getUser?.());

    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
      },
      body: JSON.stringify({ room, user: resolvedUser }),
    });

    if (!response.ok) {
      throw new Error(`Liveblocks auth failed: ${response.status}`);
    }

    return response.json();
  };
